import {
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  formatDate,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { Wallet } from 'lucide-react';
import { type JSX, useState } from 'react';

import { ApiError } from '../../api/client';
import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import { formatPriceCents } from '../../contracts/format-price';
import { searchAllCustomers } from '../../customers/api';
import { getPayment, voidPayment } from '../../payments/api';
import { mapPaymentError } from '../../payments/payment-api-errors';
import { AllocatePaymentModal } from './components/allocate-payment-modal';
import { VoidPaymentModal } from './components/void-payment-modal';

const paymentDetailRoute = getRouteApi('/protected/payments/$id');

/**
 * `payment-receipts/spec.md` §12: detalle del recibo (retenciones,
 * imputaciones existentes), "Imputar a facturas" (`payment.register`, no
 * sobre un recibo `VOIDED`) y "Anular" (`payment.void`, motivo
 * obligatorio, restaura las facturas imputadas a su estado anterior).
 */
export function PaymentDetailPage(): JSX.Element {
  const { id } = paymentDetailRoute.useParams();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [allocateModalOpen, setAllocateModalOpen] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);

  const query = useQuery({ queryKey: ['payment', id], queryFn: () => getPayment(id) });

  const customersQuery = useQuery({
    queryKey: ['customers-for-payments'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  const voidMutation = useMutation({
    mutationFn: (reason: string) => voidPayment(id, reason),
    onSuccess: (updated) => {
      queryClient.setQueryData(['payment', id], updated);
      setVoidModalOpen(false);
      toast('success', 'Recibo anulado');
    },
  });

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <Alert tone="danger" title="Recibo no encontrado">
          <Link to="/payments" className="underline">
            Volver al listado
          </Link>
        </Alert>
      );
    }

    return (
      <Alert tone="danger" title="No pudimos cargar el recibo">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const payment = query.data;
  const customers = customersQuery.data?.items ?? [];
  const customerName =
    customers.find((customer) => customer.id === payment.customerId)?.legalName ?? '—';

  const canRegister = hasPermission(currentUser.data, 'payment.register');
  const canVoid = hasPermission(currentUser.data, 'payment.void');
  const canAllocate = canRegister && payment.status !== 'VOIDED';

  const withholdingsTotalCents = payment.withholdings.reduce(
    (sum, withholding) => sum + withholding.amountCents,
    0,
  );
  const totalApplicableCents = payment.amountReceivedCents + withholdingsTotalCents;
  const allocatedCents = payment.allocations.reduce((sum, allocation) => sum + allocation.amountCents, 0);

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Wallet}
        title={payment.receiptNumber}
        description={`${customerName} · ${formatDate(payment.paymentDate)}`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge domain="payment" status={payment.status} />
            {canAllocate ? (
              <Button onClick={() => setAllocateModalOpen(true)}>Imputar a facturas</Button>
            ) : null}
            {canVoid && payment.status !== 'VOIDED' ? (
              <Button
                variant="destructive"
                onClick={() => {
                  voidMutation.reset();
                  setVoidModalOpen(true);
                }}
              >
                Anular
              </Button>
            ) : null}
          </div>
        }
      />

      <Card>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm text-slate-500">Cliente</dt>
            <dd className="text-sm font-medium text-slate-900">{customerName}</dd>
          </div>
          <div>
            <dt className="text-sm text-slate-500">Método</dt>
            <dd className="text-sm font-medium text-slate-900">
              {payment.method.name} · {payment.referenceNumber}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-slate-500">Monto recibido</dt>
            <dd className="text-sm font-medium text-slate-900">
              {formatPriceCents(payment.amountReceivedCents)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-slate-500">Total aplicable</dt>
            <dd className="text-sm font-medium text-slate-900">
              {formatPriceCents(totalApplicableCents)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-slate-500">Imputado</dt>
            <dd className="text-sm font-medium text-slate-900">{formatPriceCents(allocatedCents)}</dd>
          </div>
          <div>
            <dt className="text-sm text-slate-500">Sin imputar</dt>
            <dd className="text-sm font-medium text-slate-900">
              {formatPriceCents(payment.unallocatedCents)}
            </dd>
          </div>
        </dl>
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-semibold text-slate-900">Retenciones y percepciones</h2>
        {payment.withholdings.length === 0 ? (
          <p className="text-sm text-slate-500">Este recibo no tiene retenciones.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {payment.withholdings.map((withholding) => (
              <li
                key={withholding.id}
                className="grid grid-cols-1 gap-1 border-b border-slate-100 pb-2 text-sm last:border-b-0 sm:grid-cols-3"
              >
                <span className="font-medium text-slate-900">
                  {withholding.type.name}
                </span>
                <span className="text-slate-500">Certificado {withholding.certificateNumber}</span>
                <span className="text-slate-900">{formatPriceCents(withholding.amountCents)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-semibold text-slate-900">Imputaciones</h2>
        {payment.allocations.length === 0 ? (
          <EmptyState icon={Wallet} title="Este recibo todavía no fue imputado a ninguna factura." />
        ) : (
          <ul className="flex flex-col gap-2">
            {payment.allocations.map((allocation) => (
              <li
                key={allocation.id}
                className="grid grid-cols-1 gap-1 border-b border-slate-100 pb-2 text-sm last:border-b-0 sm:grid-cols-2"
              >
                <Link
                  to="/invoices/$id"
                  params={{ id: allocation.invoiceId }}
                  className="font-medium text-slate-900 hover:underline"
                >
                  {allocation.invoiceNumber}
                </Link>
                <span className="text-slate-900">{formatPriceCents(allocation.amountCents)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {allocateModalOpen ? (
        <AllocatePaymentModal
          paymentId={payment.id}
          unallocatedCents={payment.unallocatedCents}
          onCancel={() => setAllocateModalOpen(false)}
          onAllocated={(updated) => {
            queryClient.setQueryData(['payment', id], updated);
            setAllocateModalOpen(false);
          }}
        />
      ) : null}

      {voidModalOpen ? (
        <VoidPaymentModal
          submitting={voidMutation.isPending}
          error={voidMutation.isError ? mapPaymentError(voidMutation.error) : undefined}
          onCancel={() => setVoidModalOpen(false)}
          onConfirm={(reason) => voidMutation.mutate(reason)}
        />
      ) : null}
    </section>
  );
}
